import { Navigate, useParams } from 'react-router-dom';
import { StatCard } from '@/components/ui/StatCard';
import { KpiGrid } from '@/components/ui/KpiGrid';
import InboxIcon from '@mui/icons-material/Inbox';
import PublishIcon from '@mui/icons-material/Publish';
import ChatIcon from '@mui/icons-material/Chat';
import {
  getPlatformById,
  PLATFORM_TAB_LABELS,
  type PlatformTabId,
} from '../config/platformCapabilities';
import { SocialPlatformShell } from '../components/SocialPlatformShell';
import { SocialInboxWorkspace } from '../components/SocialInboxWorkspace';
import { SocialCommentsWorkspace } from '../components/SocialCommentsWorkspace';
import { SocialPostsWorkspace } from '../components/SocialPostsWorkspace';
import { SocialReviewsPage } from '../reviews/SocialReviewsPage';
import { SocialAnalyticsCenterPage } from '../analytics/SocialAnalyticsCenterPage';
import { useSocialAccounts } from '../hooks/useSocialAccounts';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/Button';

function PlatformOverview({
  platformId,
  platformLabel,
  apiPlatform,
  connected,
}: {
  platformId: string;
  platformLabel: string;
  apiPlatform: string;
  connected: boolean;
}) {
  return (
    <div className="space-y-4">
      <KpiGrid columns={3}>
        <StatCard title="Status" value={connected ? 'Connected' : 'Setup required'} icon={<InboxIcon />} />
        <StatCard title="Publishing" value={connected ? 'Ready' : '—'} icon={<PublishIcon />} />
        <StatCard title="Engagement" value={connected ? 'Live' : '—'} icon={<ChatIcon />} />
      </KpiGrid>
      {!connected && (
        <div className="card border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40">
          <p className="text-sm text-amber-900 dark:text-amber-100">
            Connect {platformLabel} using the setup wizard in Connection Center.
          </p>
          <Link to={`/social/connect/wizard/${platformId}`} className="mt-2 inline-block">
            <Button size="sm">Open setup wizard</Button>
          </Link>
        </div>
      )}
      <p className="text-sm text-slate-600 dark:text-slate-400">
        Workspace scoped to {apiPlatform}. Unsupported capabilities are hidden per platform policy.
      </p>
    </div>
  );
}

function PlaceholderPanel({
  title,
  detail,
  platformId,
}: {
  title: string;
  detail: string;
  platformId?: string;
}) {
  return (
    <div className="card p-6">
      <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">{title}</h3>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{detail}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {platformId && (
          <Link to={`/social/connect/guide/${platformId}`}>
            <Button size="sm" variant="secondary">
              Read setup guide
            </Button>
          </Link>
        )}
        <Link to="/social/connect?tab=health">
          <Button size="sm">Check connection health</Button>
        </Link>
        <Link to="/social/automation">
          <Button size="sm" variant="secondary">
            Automation Center
          </Button>
        </Link>
      </div>
    </div>
  );
}

export function PlatformWorkspacePage() {
  const { platformId, tab } = useParams<{ platformId: string; tab?: string }>();
  const platform = platformId ? getPlatformById(platformId) : undefined;
  const { byPlatform } = useSocialAccounts();

  if (!platform) {
    return <Navigate to="/social" replace />;
  }

  const activeTab = (tab ?? 'overview') as PlatformTabId;
  if (!platform.tabs.includes(activeTab)) {
    return <Navigate to={`/social/platforms/${platform.id}/${platform.tabs[0]}`} replace />;
  }

  const tabs = platform.tabs.map((id) => ({
    id,
    label: PLATFORM_TAB_LABELS[id],
    to: `/social/platforms/${platform.id}/${id}`,
  }));

  const isConnected = byPlatform(platform.apiPlatform).length > 0;

  const renderTab = () => {
    const pf = platform.apiPlatform;

    switch (activeTab) {
      case 'overview':
        return (
          <PlatformOverview
            platformId={platform.id}
            platformLabel={platform.label}
            apiPlatform={pf}
            connected={isConnected}
          />
        );
      case 'inbox':
      case 'messages':
        if (!platform.dmInbox) {
          return (
            <PlaceholderPanel
              title="Not available"
              detail={`${platform.label} does not support direct messages in this workspace. Use Comments or Publishing instead.`}
              platformId={platform.id}
            />
          );
        }
        return (
          <SocialInboxWorkspace
            platformFilter={pf}
            title={`${platform.label} inbox`}
            showSimulate={isConnected}
          />
        );
      case 'posts':
      case 'publishing':
        if (!platform.publishing) {
          return <Navigate to="/social/publishing" replace />;
        }
        return <SocialPostsWorkspace platformFilter={pf} />;
      case 'comments':
        if (!platform.comments) return <Navigate to="/social/comments" replace />;
        return <SocialCommentsWorkspace platformFilter={pf} />;
      case 'reviews':
        if (!platform.reviews)
          return (
            <PlaceholderPanel
              title="Reviews"
              detail="Reviews for this network are managed in Reviews Center or Google Business workspace."
              platformId={platform.id}
            />
          );
        return <SocialReviewsPage embedded />;
      case 'analytics':
        return <SocialAnalyticsCenterPage embedded />;
      case 'stories':
        return (
          <PlaceholderPanel
            title="Stories"
            detail="Stories appear here after your Facebook or Instagram account is connected with story permissions."
            platformId={platform.id}
          />
        );
      case 'reels':
        return (
          <PlaceholderPanel
            title="Reels"
            detail="Track and publish Reels once Instagram is connected from Connection Center."
            platformId={platform.id}
          />
        );
      case 'mentions':
        return (
          <PlaceholderPanel
            title="Mentions"
            detail="Mentions sync from Listening keywords — set those up under Social → Listening."
            platformId={platform.id}
          />
        );
      case 'replies':
        return <SocialCommentsWorkspace platformFilter={pf} />;
      case 'videos':
        return <SocialPostsWorkspace platformFilter={pf} showComposeLink />;
      case 'templates':
      case 'broadcasts':
      case 'contacts':
      case 'campaigns':
        return (
          <PlaceholderPanel
            title={PLATFORM_TAB_LABELS[activeTab]}
            detail={`WhatsApp ${PLATFORM_TAB_LABELS[activeTab]} requires an approved business account. Complete setup in Connection Center first.`}
            platformId={platform.id}
          />
        );
      case 'automation':
        return (
          <Navigate to="/social/automation" replace />
        );
      default:
        return (
          <PlatformOverview
            platformId={platform.id}
            platformLabel={platform.label}
            apiPlatform={pf}
            connected={isConnected}
          />
        );
    }
  };

  return (
    <SocialPlatformShell platform={platform} activeTab={activeTab} tabs={tabs}>
      {renderTab()}
    </SocialPlatformShell>
  );
}
